/* eslint-disable no-nested-ternary */
import { notifications } from '@mantine/notifications';
import { useSelector, useDispatch } from 'react-redux';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Page,
  ProcessHeader,
  DataSummaryCard,
  BorrowerDetailsTable,
} from '@/components';
import { yearsToMonths } from '@/helpers/date';
import { getMutationResult } from '@/helpers/query';
import {
  useGetAllSupportTypeMutation,
  useSaveMutation,
} from '@/services/api';
import { getRouteById, routeWithParams } from '@/helpers/routes';
import routes from '@/routes';
import { useIdInStoreGuard } from '@/hooks/useIdfromUrl';
import { roundNumber, sliceToDecimals } from '@/helpers/number';
import { formatNumberWithCommas } from '@/helpers/string';
import TableSimple from '@/components/TableSimple';
import ProcessButton from '@/components/ProcessButton';
import HandshakeIcon from '@/assets/images/handshake.svg?react'; // eslint-disable-line import/no-unresolved
import classes from './summary.module.css';
import { updateExtraDetails } from '@/data/features/proposal/proposal';

const Summary = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const borrowersStore = useSelector((state) => state.proposal.borrower);
  const proposal = useSelector((state) => state.proposal);
  const proposalOffer = useSelector((state) => state.proposalOffer);
  const user = useSelector((state) => state.user);
  const mortageCycleDetails = useSelector((state) => state.mortgageCycle);
  const [loading, setLoading] = useState(false);
  const [apiGetAllSupportTypes] = useGetAllSupportTypeMutation();
  const [apiSave] = useSaveMutation();
  const { extraDetails } = proposal;
  const isRecycling = extraDetails.isRecycling === 'yes';

  const dispatch = useDispatch();

  const { disposalboard } = proposalOffer;

  let boxesTitles = {
    boxA: '',
    boxB: '',
  };
  const { id } = proposal;
  useIdInStoreGuard();

  const handleContinue = async () => {
    if (user?.role?.code === '5') {
      try {
        setLoading(true);
        let supportType;
        const { data: result } = await getMutationResult({}, apiGetAllSupportTypes);
        const { success, data } = result;
        if (success) {
          supportType = data.find((option) => option.name === 'selfSupport');

          dispatch(updateExtraDetails({
            routeType: supportType,
          }));

          const objToSave = {
            id,
            routeType: supportType?._id,
          };

          await apiSave(objToSave);
        } else {
          notifications.show({
            title: t('globals.error.title'),
            message: t('globals.error.message'),
          });
        }
      } catch (error) {
        notifications.show({
          title: t('globals.error.title'),
          message: t('globals.error.message'),
        });
      } finally {
        setLoading(false);
      }
      navigate(routeWithParams(getRouteById(routes.newProposal.id, 'pay').path, { id }));
    } else {
      navigate(routeWithParams(getRouteById(routes.newProposal.id, 'routeType').path, { id }));
    }
  };

  const handleReturn = () => {
    navigate(routeWithParams(getRouteById(routes.newProposal.id, 'borrowersDetails').path, { id }));
  };

  const sumOfReturnsOfFirstOption = disposalboard?.[0].totalReturn;
  const sumOfReturnsOfSecondOption = disposalboard?.[1]
    ? disposalboard[1].totalReturn : 0;

  const sumOfReturnsOfInterestFirstOption = disposalboard?.[0]?.totalInterestReturn;
  const sumOfReturnsOfInterestSecondOption = disposalboard?.[1] ? disposalboard?.[1]?.totalInterestReturn : '';

  let updatedLoanAmount = extraDetails?.loanAmount;
  let maxPeriod = yearsToMonths(extraDetails?.period);

  if (isRecycling) {
    updatedLoanAmount += mortageCycleDetails?.currentLoans?.reduce((sum, loan) => sum
    + (loan.totalAmount || 0), 0) || 0;
    maxPeriod = disposalboard[0]?.disposalBoard?.length || 0;
  }

  const details = [
    {
      label: t('pages.summary.label.loanType'),
      value: extraDetails?.loanTypeNameHebrew,
    },
    {
      label: t('pages.summary.label.loanAmount'),
      value: `${formatNumberWithCommas(roundNumber(updatedLoanAmount))} ₪`,
    },
    isRecycling && {
      label: t('pages.summary.label.currentLoanAmount'),
      value: extraDetails?.currentLoanAmount,
    },
    {
      label: t('pages.summary.label.paymentPeriodInMonths'),
      value: maxPeriod,
    },
    {
      label: t('pages.summary.label.borrowerNumber'),
      value: borrowersStore?.length,
    },
  ].filter(Boolean);

  const planADetails = [
    `${formatNumberWithCommas(roundNumber(updatedLoanAmount))} ₪`,
    `${formatNumberWithCommas(roundNumber(disposalboard?.[0]?.disposalBoard[0]?.totalMonthlyReturn))} ₪`,
    `${formatNumberWithCommas(roundNumber(disposalboard?.[0]?.maxMonthlyReturn))} ₪`,
    `${formatNumberWithCommas(roundNumber(sumOfReturnsOfInterestFirstOption))} ₪`,
    `${formatNumberWithCommas(roundNumber(sumOfReturnsOfFirstOption))} ₪`,
    `${sliceToDecimals(Number(sumOfReturnsOfFirstOption) / Number(updatedLoanAmount).toFixed(2), 2)} ₪`,
  ];

  const planBDetails = [
    `${formatNumberWithCommas(proposal?.loanAmount)} ₪`,
    `${formatNumberWithCommas(roundNumber(disposalboard?.[1]?.disposalBoard[0]?.totalMonthlyReturn))} ₪`,
    `${formatNumberWithCommas(roundNumber(disposalboard?.[1]?.maxMonthlyReturn))} ₪`,
    `${formatNumberWithCommas(roundNumber(sumOfReturnsOfInterestSecondOption))} ₪`,
    `${formatNumberWithCommas(roundNumber(sumOfReturnsOfSecondOption))} ₪`,
    `${sliceToDecimals(disposalboard?.[1]?.returnConversion, 2)} ₪`,
  ];

  const plansTableHead = [
    t('pages.summary.label.loanAmount'),
    t('pages.summary.label.paymentPerMonth'),
    t('pages.summary.label.maxPaymentPerMonth'),
    t('pages.summary.label.sumOfReturnsOfInterest'),
    t('pages.summary.label.totalPaymentInPeriodEnd'),
    t('pages.summary.label.forEvryShekelYouWillPay'),
  ];

  const tableHead = [
    t('pages.summary.table.head.month'),
    t('pages.summary.table.head.remaining'),
    t('pages.summary.table.head.monthlyPaymentWithoutInterest'),
    t('pages.summary.table.head.monthlyPaymentInterest'),
    t('pages.summary.table.head.totalPayment'),
  ];

  const risk0 = disposalboard?.[0]?.risk ?? -1; // Default to -1 if undefined
  const risk1 = disposalboard?.[1]?.risk ?? -1; // Default to -1 if undefined

  if (disposalboard?.length === 1) {
    // Single-element cases
    boxesTitles = {
      boxATitle: 'פרטי התוכנית המומלצת עבורך',
      boxBTitle: 'פרטי התוכנית בסיכון גבוה יותר',
    };
  } else {
    // Multi-element cases
    boxesTitles = {
      boxATitle:
        risk0 === 0 ? t('pages.offer.box.title.regular')
          : risk0 === 1 ? t('pages.offer.box.title.medium')
            : risk0 === 2 ? t('pages.offer.box.title.high')
              : '',
      boxBTitle:
        risk1 === 0 ? t('pages.offer.box.title.regular')
          : risk1 === 1 ? t('pages.offer.box.title.medium')
            : risk1 === 2 ? t('pages.offer.box.title.high')
              : '',
    };
  }

  const tableBodyA = proposalOffer?.disposalboard?.[0]?.disposalBoard?.map((item) => [
    item?.period,
    `${formatNumberWithCommas(roundNumber(item.fundBalance)) || 0} ₪`,
    `${formatNumberWithCommas(roundNumber(item.monthlyFundReturn))} ₪`,
    `${formatNumberWithCommas(roundNumber(item?.monthlyInterestReturn))} ₪`,
    `${formatNumberWithCommas(roundNumber(item?.totalMonthlyReturn))} ₪`,
  ]);

  const tableBodyB = proposalOffer?.disposalboard?.[1]?.disposalBoard?.map((item) => [
    item?.period,
    `${formatNumberWithCommas(roundNumber(item.fundBalance)) || 0} ₪`,
    `${formatNumberWithCommas(roundNumber(item.monthlyFundReturn))} ₪`,
    `${formatNumberWithCommas(roundNumber(item?.monthlyInterestReturn))} ₪`,
    `${formatNumberWithCommas(roundNumber(item?.totalMonthlyReturn))} ₪`,
  ]);

  const planADisposalboardtableData = {
    head: tableHead,
    body: tableBodyA,
  };

  const planBDisposalboardtableData = {
    head: tableHead,
    body: tableBodyB,
  };

  const planADetailsTable = {
    head: plansTableHead,
    body: [planADetails],
  };
  const planBDetailsTable = {
    head: plansTableHead,
    body: [planBDetails],
  };

  return (
    <ProcessHeader
      headerText={t('pages.summary.title')}
      headerColor="#DBFFE1"
      headerTextColor="#00A81C"
      subtitle={t('pages.summary.subtitle')}
      showReturnButton
      onReturn={handleReturn}
      icon={HandshakeIcon}
    >
      <div>
        <DataSummaryCard details={details} />
      </div>

      <div>
        <h3>{t('pages.summary.title.borrowerDetails')}</h3>
        <BorrowerDetailsTable borrowers={borrowersStore} />
      </div>

      {disposalboard?.length > 0 && (
        <>
          <h3>{boxesTitles.boxATitle}</h3>
          <div className={classes.summaryContainer}>
            <TableSimple data={planADetailsTable} />
            <div className={classes.summaryCollapse}>
              <Page.Section
                title={`${t('pages.summary.title.disposalBoardFor')}${boxesTitles.boxATitle}`}
                withCollapse
                withBackground
                startClose>
                <TableSimple data={planADisposalboardtableData} />
              </Page.Section>
            </div>
          </div>
        </>
      )
      }

      { disposalboard?.length > 1 && (<>
        <h3>{boxesTitles.boxBTitle}</h3>
        <div className={classes.summaryContainer}>
          <TableSimple data={planBDetailsTable} />
          <div className={classes.summaryCollapse}>
            <Page.Section
                  title={`${t('pages.summary.title.disposalBoardFor')}${boxesTitles.boxBTitle}`}
                  withCollapse
                  withBackground
                  startClose>
              <TableSimple data={planBDisposalboardtableData} />
            </Page.Section>
          </div>
        </div>
      </>
      )
      }
      <Page.Actions>
        <ProcessButton onClick={handleContinue} loading={loading} />
      </Page.Actions>

    </ProcessHeader >
  );
};

export default Summary;
